import React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { MaintenanceGate } from "./components/MaintenanceGate";

import { JobNotificationCenter } from "./components/JobNotificationCenter";
import { GlobalAppHeader } from "./components/GlobalAppHeader";
import { AppRouteScrollReset } from "./components/AppRouteScrollReset";
import { AppNavigationGuardProvider } from "./contexts/AppNavigationGuardContext";
import { ReportCenterProvider } from "./contexts/ReportCenterContext";
import ProfileCompletionGate from "./components/ProfileCompletionGate";
import { storage } from "./lib/storage";
import BillingCancelPage from "./pages/BillingCancelPage";
import BillingSuccessPage from "./pages/BillingSuccessPage";
import CharacterSwitch from "./pages/CharacterSwitch";
import Chat from "./pages/Chat";
import CommercialTransactionsPage from "./pages/CommercialTransactionsPage";
import GoogleLinkPopup from "./pages/GoogleLinkPopup";
import GoogleOAuthPopupComplete from "./pages/GoogleOAuthPopupComplete";
import GroupTalkHub from "./pages/GroupTalkHub";
import GroupTalkPreview from "./pages/GroupTalkPreview";
import GroupTalkSessionList from "./pages/GroupTalkSessionList";
import GroupTalkSession from "./pages/GroupTalkSession";
import Home from "./pages/Home";
import KnowledgeDetailPage from "./pages/KnowledgeDetailPage";
import KnowledgeLibraryPage from "./pages/KnowledgeLibraryPage";
import KnowledgeUsePage from "./pages/KnowledgeUsePage";
import LineLinkPopup from "./pages/LineLinkPopup";
import LineEntryPage from "./pages/LineEntryPage";
import LineLoginPage from "./pages/LineLoginPage";
import LineOAuthPopupComplete from "./pages/LineOAuthPopupComplete";
import LineRegisterStart from "./pages/LineRegisterStart";
import LineRegisterCallback from "./pages/LineRegisterCallback";
import LineRegisterComplete from "./pages/LineRegisterComplete";
import EmailInviteOnboarding from "./pages/EmailInviteOnboarding";
import EmailOtpEmailPage from "./pages/EmailOtpEmailPage";
import EmailOtpVerifyPage from "./pages/EmailOtpVerifyPage";
import ExecutiveMemoDetailPage from "./pages/ExecutiveMemoDetailPage";
import ExecutiveMemosPage from "./pages/ExecutiveMemosPage";
import LandingPage from "./pages/LandingPage";
import PlansPage from "./pages/PlansPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import PresidentConsultPage from "./pages/PresidentConsultPage";
import SalesSupportPage from "./pages/SalesSupportPage";
import ProfileLoginMethodsPage from "./pages/ProfileLoginMethodsPage";
import ProfileSettings from "./pages/ProfileSettings";
import ReceiptDetailPage from "./pages/ReceiptDetailPage";
import SupportInquiryPage from "./pages/SupportInquiryPage";
import ReceiptHistoryPage from "./pages/ReceiptHistoryPage";
import ReceiptPopup from "./pages/ReceiptPopup";
import RequestCompanyResearchJobDetailPage from "./pages/RequestCompanyResearchJobDetailPage";
import RequestCompanyResearchJobListPage from "./pages/RequestCompanyResearchJobListPage";
import RequestCompanyResearchPage from "./pages/RequestCompanyResearchPage";
import RequestDeepReportPage from "./pages/RequestDeepReportPage";
import ReportListPage from "./pages/ReportListPage";
import ReportDetailPage from "./pages/ReportDetailPage";
import RequestEntry from "./pages/RequestEntry";
import RequestExecutiveMemosPage from "./pages/RequestExecutiveMemosPage";
import RequestNewsPage from "./pages/RequestNewsPage";
import RequestSchedulesPage from "./pages/RequestSchedulesPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import Splash from "./pages/Splash";
import TermsPage from "./pages/TermsPage";

const IS_STAGING = process.env.REACT_APP_ENV === "staging";
const STAGING_BANNER_HEIGHT_PX = 24;
const PUBLIC_LANDING_PAGE_PATH = "/landing";

function requireProfile(element: React.ReactElement) {
  return <ProfileCompletionGate>{element}</ProfileCompletionGate>;
}

// 未ログインのトップページは公開LPを表示し、ログイン済みは既存の起動フロー（/splash → /home）へ送る。
function PublicRootEntry() {
  const { accessToken, refreshToken } = storage.getAuth();
  if (accessToken || refreshToken) {
    return <Navigate to="/splash" replace />;
  }
  return <LandingPage />;
}

export default function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <AppRouteScrollReset />
      {IS_STAGING && (
        <>
          {/* position: fixedのバナーはページ内アンカー(#evidence-1等)への
              ネイティブジャンプで考慮されず、ジャンプ先の上端が隠れて見えるため
              scroll-padding-topで補正する。 */}
          <style>{`html { scroll-padding-top: ${STAGING_BANNER_HEIGHT_PX}px; }`}</style>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              height: `${STAGING_BANNER_HEIGHT_PX}px`,
              background: "#f97316",
              color: "#fff",
              fontSize: "11px",
              fontWeight: "bold",
              letterSpacing: "0.1em",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
          >
            STAGING
          </div>
        </>
      )}
      <MaintenanceGate>
      <AppNavigationGuardProvider>
      <ReportCenterProvider>
      <JobNotificationCenter />
      <GlobalAppHeader />
      <Routes>
        <Route path="/" element={<PublicRootEntry />} />
        <Route path={PUBLIC_LANDING_PAGE_PATH} element={<LandingPage />} />
        <Route path="/sales-support" element={<SalesSupportPage />} />
        <Route path="/president-consult" element={<PresidentConsultPage />} />
        <Route path="/knowledge-use" element={<KnowledgeUsePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/splash" element={<Splash />} />
        <Route path="/onboarding" element={<EmailInviteOnboarding />} />
        <Route path="/email-otp/email" element={<EmailOtpEmailPage />} />
        <Route path="/email-otp/verify" element={<EmailOtpVerifyPage />} />
        <Route path="/home" element={requireProfile(<Home />)} />
        <Route path="/profile" element={<ProfileSettings />} />
        <Route path="/profile/login-methods" element={<ProfileLoginMethodsPage />} />
        <Route path="/character-switch" element={requireProfile(<CharacterSwitch />)} />
        <Route path="/talk" element={<Navigate to="/chat" replace />} />
        <Route path="/talk/group" element={requireProfile(<GroupTalkHub />)} />
        <Route path="/talk/group/preview" element={requireProfile(<GroupTalkPreview />)} />
        <Route path="/talk/group/sessions" element={requireProfile(<GroupTalkSessionList />)} />
        <Route path="/talk/group/sessions/:sessionId" element={requireProfile(<GroupTalkSession />)} />
        <Route path="/request" element={requireProfile(<RequestEntry />)} />
        <Route path="/request/company-research" element={requireProfile(<RequestCompanyResearchPage />)} />
        <Route path="/request/company-research/jobs" element={requireProfile(<RequestCompanyResearchJobListPage />)} />
        <Route path="/request/company-research/jobs/:jobId" element={requireProfile(<RequestCompanyResearchJobDetailPage />)} />
        <Route path="/request/knowledge" element={requireProfile(<KnowledgeLibraryPage />)} />
        <Route path="/request/knowledge/:knowledgeItemId" element={requireProfile(<KnowledgeDetailPage />)} />
        <Route path="/request/executive-memos" element={requireProfile(<RequestExecutiveMemosPage />)} />
        <Route path="/request/handwritten-notes" element={<Navigate to="/request" replace />} />
        <Route path="/request/news" element={requireProfile(<RequestNewsPage />)} />
        <Route path="/request/report" element={<Navigate to="/request/deep-report" replace />} />
        <Route path="/request/deep-report" element={requireProfile(<RequestDeepReportPage />)} />
        <Route path="/reports" element={requireProfile(<ReportListPage />)} />
        <Route path="/reports/:runId" element={requireProfile(<ReportDetailPage />)} />
        <Route path="/request/schedules" element={requireProfile(<RequestSchedulesPage />)} />
        <Route path="/executive-memos" element={requireProfile(<ExecutiveMemosPage />)} />
        <Route path="/executive-memos/:memoId" element={requireProfile(<ExecutiveMemoDetailPage />)} />
        {/* AI社長室の通常利用前に必須プロファイル登録を求めるため、OCR系のreceipt導線もgate対象にする。 */}
        <Route path="/receipt" element={requireProfile(<ReceiptPopup />)} />
        <Route path="/receipt/history" element={requireProfile(<ReceiptHistoryPage />)} />
        <Route path="/receipt/:jobId" element={requireProfile(<ReceiptDetailPage />)} />
        <Route path="/google-link" element={<GoogleLinkPopup />} />
        <Route path="/google-link/complete" element={<GoogleOAuthPopupComplete />} />
        <Route path="/line-link" element={<LineLinkPopup />} />
        <Route path="/line-link/complete" element={<LineOAuthPopupComplete />} />
        <Route path="/line" element={<LineEntryPage />} />
        <Route path="/line-login" element={<LineLoginPage />} />
        <Route path="/line-register" element={<LineRegisterStart />} />
        <Route path="/line-register/callback" element={<LineRegisterCallback />} />
        <Route path="/line-register/complete" element={<LineRegisterComplete />} />
        <Route path="/chat" element={requireProfile(<Chat />)} />
        <Route path="/billing/success" element={<BillingSuccessPage />} />
        <Route path="/billing/cancel" element={<BillingCancelPage />} />
        <Route path="/plans" element={<PlansPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/commercial-transactions" element={<CommercialTransactionsPage />} />
        <Route path="/support-inquiry" element={<SupportInquiryPage />} />
        <Route path="*" element={<Navigate to="/splash" replace />} />
      </Routes>
      </ReportCenterProvider>
      </AppNavigationGuardProvider>
      </MaintenanceGate>
    </BrowserRouter>
    </ErrorBoundary>
  );
}
